import { Modal, Spin, Tabs, Descriptions, Empty, Table, Tag, Button, List } from "antd";
import React, { FC, useState } from "react";
import {
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Clock,
  Bell,
  Users,
  Calendar,
  FileText,
  Link2,
  UserCheck,
  UserX,
  Receipt,
  Download,
  Eye,
} from "lucide-react";
import dayjs from "dayjs";
import { useGetClient } from "~/services/client";
import { useClientStore } from "./clients.store";
import { capitalize, getClientName } from "~/utils";
import { ClientStatus, SortOrder } from "~/utils/constants";
import { TClient, TFile } from "~/types";
import { useStorageInvoices } from "~/services/storage/storage.queries";
import { StorageInvoice, StorageInvoiceStatus } from "~/services/storage/storage.api";
import { useGetFiles } from "~/services/file";
import { useNotification } from "~/hooks";
import { useGetFileMimetype } from "~/services/common";

export const ClientProfileModal: FC = () => {
  const { profileModal, closeProfileModal, openClientModal } = useClientStore();
  const { data: client, isLoading } = useGetClient(profileModal.clientId);

  const formatAddress = (address: any) => {
    if (!address) return "Not provided";
    const parts = [
      address.address,
      address.apt,
      address.city,
      address.state,
      address.zip,
      address.country,
    ].filter(Boolean);
    return parts.join(", ") || "Not provided";
  };

  const formatTime = (time: Date | string | undefined) => {
    if (!time) return null;
    return dayjs(time).format("h:mm A");
  };

  const tabItems = [
    {
      key: "overview",
      label: (
        <span className="flex items-center gap-2">
          <Building2 size={14} />
          Overview
        </span>
      ),
      children: client && <OverviewTab client={client} formatAddress={formatAddress} />,
    },
    {
      key: "addresses",
      label: (
        <span className="flex items-center gap-2">
          <MapPin size={14} />
          Addresses
        </span>
      ),
      children: client && <AddressesTab client={client} formatAddress={formatAddress} />,
    },
    {
      key: "rates",
      label: (
        <span className="flex items-center gap-2">
          <DollarSign size={14} />
          Rates & Billing
        </span>
      ),
      children: client && <RatesTab client={client} />,
    },
    {
      key: "preferences",
      label: (
        <span className="flex items-center gap-2">
          <Bell size={14} />
          Preferences
        </span>
      ),
      children: client && <PreferencesTab client={client} formatTime={formatTime} />,
    },
    {
      key: "notes",
      label: (
        <span className="flex items-center gap-2">
          <FileText size={14} />
          Notes ({client?.notes?.length || 0})
        </span>
      ),
      children: client && <NotesTab client={client} />,
    },
    {
      key: "documents",
      label: (
        <span className="flex items-center gap-2">
          <FileText size={14} />
          Documents
        </span>
      ),
      children: client?.id && <DocumentsTab clientId={client.id} />,
    },
    {
      key: "storage-invoices",
      label: (
        <span className="flex items-center gap-2">
          <Receipt size={14} />
          Storage Invoices
        </span>
      ),
      children: client && <StorageInvoicesTab clientId={client.id} />,
    },
  ];

  return (
    <Modal
      open={profileModal.open}
      onCancel={closeProfileModal}
      footer={
        <div className="flex justify-end gap-2">
          <button
            onClick={closeProfileModal}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={() => {
              closeProfileModal();
              openClientModal(client?.id);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Edit Client
          </button>
        </div>
      }
      width={900}
      title={
        <div className="flex items-center justify-between pr-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Building2 size={20} className="text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold">{client?.companyName || "Client Profile"}</span>
                {client?.clientRef && (
                  <span className="text-sm text-gray-500">({client.clientRef})</span>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm">
                {client?.type && (
                  <span className="px-2 py-0.5 text-xs border rounded bg-white text-gray-600">
                    {capitalize(client.type)}
                  </span>
                )}
                {client?.status && (
                  <span className={`px-2 py-0.5 text-xs border rounded ${
                    client.status === ClientStatus.ACTIVE
                      ? "border-green-300 text-green-700 bg-green-50"
                      : "border-red-300 text-red-700 bg-red-50"
                  }`}>
                    {capitalize(client.status)}
                  </span>
                )}
                {client?.isSubClient && (
                  <span className="px-2 py-0.5 text-xs border rounded bg-white text-gray-600">
                    Subclient
                  </span>
                )}
                {client?.user ? (
                  <span className="px-2 py-0.5 text-xs border border-green-300 rounded bg-green-50 text-green-700 flex items-center gap-1">
                    <UserCheck size={11} />
                    User Account
                  </span>
                ) : (
                  <span className="px-2 py-0.5 text-xs border rounded bg-white text-gray-500 flex items-center gap-1">
                    <UserX size={11} />
                    No User Account
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      }
      destroyOnClose
    >
      <Spin spinning={isLoading}>
        {client ? (
          <Tabs items={tabItems} className="client-profile-tabs" />
        ) : (
          <Empty description="Client not found" />
        )}
      </Spin>
    </Modal>
  );
};

// Overview Tab
const OverviewTab: FC<{ client: TClient; formatAddress: (addr: any) => string }> = ({ client, formatAddress }) => {
  return (
    <div className="space-y-5">
      {/* Company Info */}
      <div>
        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
          <Building2 size={14} />
          Company Information
        </h3>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="Company Name">{client.companyName || "-"}</Descriptions.Item>
          <Descriptions.Item label="Client Ref">{client.clientRef || "-"}</Descriptions.Item>
          <Descriptions.Item label="Type">{client.type ? capitalize(client.type) : "-"}</Descriptions.Item>
          <Descriptions.Item label="Status">
            {client.status ? (
              <span className={client.status === ClientStatus.ACTIVE ? "text-green-600" : "text-red-600"}>
                {capitalize(client.status)}
              </span>
            ) : "-"}
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/* Primary Contact */}
      <div>
        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
          <User size={14} />
          Primary Contact
        </h3>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="Name">{getClientName(client) || "-"}</Descriptions.Item>
          <Descriptions.Item label="Email">
            {client.email ? (
              <a href={`mailto:${client.email}`} className="text-blue-600 hover:underline">
                {client.email}
              </a>
            ) : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            {client.contactPhone ? (
              <a href={`tel:${client.contactPhone}`} className="text-blue-600 hover:underline">
                {client.contactPhone}
              </a>
            ) : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Company Direct">{client.companyDirectPhone || "-"}</Descriptions.Item>
          <Descriptions.Item label="Company Mobile" span={2}>{client.companyMobile || "-"}</Descriptions.Item>
        </Descriptions>
      </div>

      {/* User Account Status */}
      <div>
        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
          {client.user ? <UserCheck size={14} /> : <UserX size={14} />}
          User Account
        </h3>
        {client.user ? (
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="Status">
              <span className="text-green-600">Active</span>
            </Descriptions.Item>
            <Descriptions.Item label="User Email">{client.user.email || client.email || "-"}</Descriptions.Item>
            <Descriptions.Item label="Role">{client.user.role ? capitalize(client.user.role) : "Client"}</Descriptions.Item>
            <Descriptions.Item label="Portal Access">
              <span className="text-green-600">Enabled</span>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div className="text-gray-500 text-sm border rounded p-3 bg-gray-50">
            No user account. Edit client and check "Create user account" to enable portal access.
          </div>
        )}
      </div>

      {/* Client Relationship */}
      {client.isSubClient && (
        <div>
          <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
            <Link2 size={14} />
            Client Relationship
          </h3>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="Type">Subclient</Descriptions.Item>
            <Descriptions.Item label="Parent Client">{client.parentClient?.companyName || client.parentClientId || "-"}</Descriptions.Item>
            <Descriptions.Item label="Billing to Parent">{client.isBillingParent ? "Yes" : "No"}</Descriptions.Item>
          </Descriptions>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 pt-2">
        <div className="border rounded p-3 text-center">
          <div className="text-xl font-semibold text-gray-800">{client.jobTickets?.length || 0}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Bookings</div>
        </div>
        <div className="border rounded p-3 text-center">
          <div className="text-xl font-semibold text-gray-800">{client.shippingAddresses?.length || 0}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Locations</div>
        </div>
        <div className="border rounded p-3 text-center">
          <div className="text-xl font-semibold text-gray-800">{client.notes?.length || 0}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Notes</div>
        </div>
      </div>
    </div>
  );
};

// Addresses Tab
const AddressesTab: FC<{ client: TClient; formatAddress: (addr: any) => string }> = ({ client, formatAddress }) => {
  return (
    <div className="space-y-5">
      {/* Shipping Addresses */}
      <div>
        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
          <MapPin size={14} />
          Shipping Addresses ({client.shippingAddresses?.length || 0})
        </h3>
        {client.shippingAddresses && client.shippingAddresses.length > 0 ? (
          <div className="space-y-2">
            {client.shippingAddresses.map((addr, idx) => (
              <div key={idx} className="border rounded p-3">
                <div className="flex justify-between items-start mb-1">
                  <div className="font-medium text-sm">
                    {addr.addressName || `Address ${idx + 1}`}
                    {idx === 0 && <span className="ml-2 text-xs text-gray-500">(Primary)</span>}
                  </div>
                  {addr.travelTime && (
                    <span className="text-xs text-gray-500">
                      {addr.travelTime} min travel
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  <div>{addr.address}</div>
                  {addr.apt && <div>{addr.apt}</div>}
                  <div>{[addr.city, addr.state, addr.zip].filter(Boolean).join(", ")}</div>
                  {addr.country && <div>{addr.country}</div>}
                </div>
                {addr.specialInstructions && (
                  <div className="mt-2 text-xs text-gray-500 italic border-t pt-2">
                    Note: {addr.specialInstructions}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-gray-500 text-sm border rounded p-3 bg-gray-50">No shipping addresses</div>
        )}
      </div>

      {/* Billing Address */}
      <div>
        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
          <DollarSign size={14} />
          Billing Address
        </h3>
        {client.billingAddress ? (
          <div className="border rounded p-3">
            <div className="font-medium text-sm mb-1">
              {client.billingAddress.addressName || "Billing Address"}
            </div>
            <div className="text-sm text-gray-600">
              <div>{client.billingAddress.address}</div>
              {client.billingAddress.apt && <div>{client.billingAddress.apt}</div>}
              <div>{[client.billingAddress.city, client.billingAddress.state, client.billingAddress.zip].filter(Boolean).join(", ")}</div>
              {client.billingAddress.country && <div>{client.billingAddress.country}</div>}
            </div>
          </div>
        ) : (
          <div className="text-gray-500 text-sm border rounded p-3 bg-gray-50">No billing address</div>
        )}
      </div>
    </div>
  );
};

// Rates Tab
const RatesTab: FC<{ client: TClient }> = ({ client }) => {
  const clientData = client as any; // Cast to any to access additional fields

  return (
    <div className="space-y-5">
      {/* Rate Category */}
      <div>
        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
          <DollarSign size={14} />
          Rate Category
        </h3>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="Rate Type">
            {clientData.rateType ? capitalize(clientData.rateType) : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Payment Terms">
            {clientData.paymentTerms ? capitalize(clientData.paymentTerms) : "-"}
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/* Custom Adjustments */}
      <div>
        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
          Custom Rate Adjustments
        </h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="border rounded p-3 text-center">
            <div className="text-xl font-semibold text-gray-800">
              {clientData.laborRateAdjustment || 0}%
            </div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Labor Rate</div>
          </div>
          <div className="border rounded p-3 text-center">
            <div className="text-xl font-semibold text-gray-800">
              {clientData.materialsAdjustment || 0}%
            </div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Materials</div>
          </div>
          <div className="border rounded p-3 text-center">
            <div className="text-xl font-semibold text-gray-800">
              {clientData.transportAdjustment || 0}%
            </div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Transport</div>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">
          0% = Standard rate • Positive = Premium • Negative = Discount
        </p>
      </div>
    </div>
  );
};

// Preferences Tab
const PreferencesTab: FC<{ client: TClient; formatTime: (time: Date | string | undefined) => string | null }> = ({ client, formatTime }) => {
  const clientData = client as any;

  return (
    <div className="space-y-5">
      {/* Communication */}
      <div>
        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
          <Bell size={14} />
          Communication Preferences
        </h3>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Preferred Method">
            {clientData.preferredCommunicationMethod ? capitalize(clientData.preferredCommunicationMethod) : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Service Notifications">
            {clientData.serviceNotifications?.length > 0 ? (
              <span className="text-gray-700">
                {clientData.serviceNotifications.join(", ")}
              </span>
            ) : "-"}
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/* Staff Preferences */}
      <div>
        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
          <Users size={14} />
          Staff Preferences
        </h3>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="Preferred Team">
            {clientData.team?.name || clientData.teamId || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Avoid Staff">
            {clientData.staffIds?.length > 0 ? `${clientData.staffIds.length} staff` : "-"}
          </Descriptions.Item>
        </Descriptions>
      </div>

      {/* Timing Preferences */}
      <div>
        <h3 className="font-medium text-gray-700 mb-2 flex items-center gap-2 text-sm uppercase tracking-wide">
          <Calendar size={14} />
          Timing Preferences
        </h3>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="Preferred Days">
            {clientData.preferredDays?.length > 0 ? (
              <span className="text-gray-700">
                {clientData.preferredDays.join(", ")}
              </span>
            ) : "No preference"}
          </Descriptions.Item>
          <Descriptions.Item label="Time Window">
            {client.preferredTimeWindowFrom || client.preferredTimeWindowTo ? (
              <span>
                {formatTime(client.preferredTimeWindowFrom) || "Any"} - {formatTime(client.preferredTimeWindowTo) || "Any"}
              </span>
            ) : "No preference"}
          </Descriptions.Item>
        </Descriptions>
      </div>
    </div>
  );
};

// Notes Tab
const NotesTab: FC<{ client: TClient }> = ({ client }) => {
  return (
    <div>
      {client.notes && client.notes.length > 0 ? (
        <div className="space-y-2">
          {client.notes.map((note, idx) => (
            <div key={idx} className="border rounded p-3">
              <div className="text-gray-800 text-sm">{note.content}</div>
              <div className="mt-2 text-xs text-gray-500 flex items-center gap-2 border-t pt-2">
                <User size={11} />
                {getClientName(note.author)}
                <span>•</span>
                {dayjs(note.createdAt).format("MMM D, YYYY h:mm A")}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty description="No notes" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </div>
  );
};

// Documents Tab - Shows profile documents uploaded by client
const DocumentsTab: FC<{ clientId: string }> = ({ clientId }) => {
  const [selectedDoc, setSelectedDoc] = useState<TFile | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const { error } = useNotification();

  const {
    data: filesData,
    isLoading,
  } = useGetFiles({
    clientId,
    pageSize: 100,
    sortByCreatedAt: SortOrder.DESC,
  });

  // Filter to show only profile documents (files without jobTicketId)
  const profileDocuments = filesData?.items?.filter(
    (file) => !file.jobTicket?.id
  ) || [];

  const viewDocument = (doc: TFile) => {
    if (!doc.filePath) {
      error({
        message: "Cannot view document",
        description: "Document URL is missing.",
      });
      return;
    }
    setSelectedDoc(doc);
    setPreviewVisible(true);
  };

  const downloadDocument = async (doc: TFile) => {
    if (!doc.filePath) {
      error({
        message: "Cannot download document",
        description: "Document URL is missing.",
      });
      return;
    }
    try {
      const response = await fetch(doc.filePath, { method: "GET" });
      if (!response.ok) throw new Error("Failed to fetch file");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.fileName || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      error({
        message: "Failed to download file",
        description: err.message,
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const DocumentPreview: FC<{ doc: TFile }> = ({ doc }) => {
    const fileUrl = `${doc.filePath}`;
    const [canLoad, setCanLoad] = useState<boolean | null>(null);
    const { data: mimeType } = useGetFileMimetype(doc.filePath);

    const canLoadFile = async (url: string) => {
      try {
        const response = await fetch(url, { method: "GET" });
        return response.ok;
      } catch (error) {
        console.error("Error checking file:", error);
        return false;
      }
    };

    React.useEffect(() => {
      canLoadFile(fileUrl).then((result) => setCanLoad(result));
    }, [fileUrl]);

    if (canLoad === null) {
      return <div className="text-center p-4">Loading...</div>;
    }

    if (!canLoad) {
      return (
        <div className="text-center p-4">
          <div>
            Unable to load the document. The file may not exist or the server is
            not running.
          </div>
        </div>
      );
    }

    switch (mimeType) {
      case "image/jpeg":
      case "image/png":
        return (
          <div className="text-center">
            <img
              src={fileUrl}
              alt={doc.title}
              className="max-w-full max-h-[60vh] object-contain rounded"
              onError={() => setCanLoad(false)}
            />
          </div>
        );
      case "application/pdf":
        return (
          <div className="w-full">
            <embed
              src={fileUrl}
              type="application/pdf"
              className="w-full h-[60vh] rounded"
              onError={() => setCanLoad(false)}
            />
          </div>
        );
      case "application/msword":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      case "text/csv":
        return (
          <div className="text-center p-4">
            <div>
              This file type ({mimeType.split("/")[1].toUpperCase()}) cannot be
              previewed in the browser.
            </div>
            <Button
              type="primary"
              href={fileUrl}
              download={doc.fileName || "document"}
              className="mt-4"
            >
              Download File
            </Button>
          </div>
        );
      default:
        return <div>Unsupported file type</div>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin />
      </div>
    );
  }

  if (profileDocuments.length === 0) {
    return (
      <Empty
        description="No documents uploaded by client"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <div className="text-sm text-gray-600">
          Documents uploaded by the client in their profile
        </div>
      </div>
      <List
        dataSource={profileDocuments}
        renderItem={(doc) => (
          <List.Item
            className="hover:bg-gray-50 transition-colors cursor-pointer border-b"
            onClick={() => viewDocument(doc)}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <FileText size={20} className="text-blue-600" />
                <div>
                  <div className="font-medium">{doc.title || "Unnamed Document"}</div>
                  {doc.type && (
                    <div className="text-xs text-gray-500 mt-1">
                      Type: {capitalize(doc.type)}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-500">
                  {formatDate(doc.createdAt)}
                </div>
                <Button
                  type="text"
                  icon={<Eye size={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    viewDocument(doc);
                  }}
                />
                <Button
                  type="text"
                  icon={<Download size={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadDocument(doc);
                  }}
                />
              </div>
            </div>
          </List.Item>
        )}
      />

      <Modal
        title={selectedDoc?.title || "View Document"}
        open={previewVisible}
        onCancel={() => {
          setPreviewVisible(false);
          setSelectedDoc(null);
        }}
        footer={[
          <Button
            key="download"
            type="primary"
            onClick={() => selectedDoc && downloadDocument(selectedDoc)}
          >
            Download File
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setPreviewVisible(false);
              setSelectedDoc(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedDoc && <DocumentPreview doc={selectedDoc} />}
      </Modal>
    </div>
  );
};

// Storage Invoices Tab
const StorageInvoicesTab: FC<{ clientId: string }> = ({ clientId }) => {
  const { data: invoicesData, isLoading } = useStorageInvoices({
    clientId,
    limit: 50,
  });

  // Filter out cancelled invoices
  const invoices = invoicesData?.invoices?.filter(
    (inv) => inv.status !== StorageInvoiceStatus.CANCELLED
  ) || [];

  const getStatusTag = (status: StorageInvoiceStatus) => {
    const config: Record<StorageInvoiceStatus, { color: string; label: string }> = {
      [StorageInvoiceStatus.DRAFT]: { color: "default", label: "Draft" },
      [StorageInvoiceStatus.SENT]: { color: "blue", label: "Sent" },
      [StorageInvoiceStatus.PAID]: { color: "green", label: "Paid" },
      [StorageInvoiceStatus.CANCELLED]: { color: "red", label: "Cancelled" },
    };
    const { color, label } = config[status];
    return <Tag color={color}>{label}</Tag>;
  };

  const columns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 200,
      render: (text: string) => (
        <span className="font-medium text-blue-600">{text}</span>
      ),
    },
    {
      title: "Period",
      key: "period",
      width: 120,
      render: (_: unknown, record: StorageInvoice) =>
        dayjs()
          .month(record.billingPeriodMonth - 1)
          .year(record.billingPeriodYear)
          .format("MMM YYYY"),
    },
    {
      title: "Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 100,
      render: (date: string) => dayjs(date).format("MM/DD/YY"),
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 100,
      align: "right" as const,
      render: (value: number | string) => (
        <span className="font-medium">${Number(value || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (status: StorageInvoiceStatus) => getStatusTag(status),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin />
      </div>
    );
  }

  if (invoices.length === 0) {
    return <Empty description="No storage invoices" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div>
      <Table
        dataSource={invoices}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ y: 300 }}
      />
      <div className="mt-3 text-xs text-gray-500 text-right">
        {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
};
